import { CaretDown, CaretUp, ChevronRight } from '@carbon/icons-react'
import React, { useEffect, useMemo, useState } from 'react'
import {
  downloadSalesforceApexTriggersReport,
  getSalesforceApexTriggers
} from '../../api/salesforceAudit/salesforceAudit.service'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { DDSTypography } from 'den-design-system'
import NoRecord from '../../components/common/NoContent/NoRecord'
import NoRecordData from '../../assets/images/setting.svg'
import NoRecordImage from '../../assets/images/folder-not-found 1.svg'
import { SalesforceAuditStore } from '../../store/SalesforceAuditStore'
import Search from '../../components/New/Search/Search'
import Spinner from '../../components/common/Spinner/Spinner'
import Table from '../../components/common/DDS/Table/Table'
import Tabs from '../../components/common/DDS/Tabs/Tabs'
import ToastNotification from '../../components/common/DDS/Toast/Toast'
import Tooltip from '../../components/New/Tooltip/Tooltip'
import { convertJsonToCsvDownload } from '../../utils/FileUtils'
import moment from 'moment'
import { observer } from 'mobx-react-lite'
import { paginate } from '../../utils/CommonUtils'
import pallete from '../../global/newPallete'
import { replaceUrl } from '../../utils/UrlUtils'
import routeConstants from '../../constants/RouteConstants'
import { timeFormats } from '../../enum/TIME'
import { timeZone } from '../../utils/DateTimeUtils'
import { useStoreContext } from '../../store/StoreContext'

const SFApexTriggersOverview: React.FC = () => {
  const navigate = useNavigate()
  const store = useStoreContext()
  const [searchParams, setSearchParams] = useSearchParams()
  const [tabIndex, setTabIndex] = React.useState<string>(
    searchParams.get('tab') ?? '1'
  )
  const [currentPageActive, setCurrentPageActive] = useState(1)
  const [currentPageInactive, setCurrentPageInactive] = useState(1)
  const { salesforceAuditStore } = useStoreContext()
  const [searchTerm, setSearchTerm] = useState('')
  const [activeSearchTerm, setActiveSearchTerm] = useState('')
  const [searchColumn, setSearchColumn] = useState('all')
  const [showDropdown, setShowDropdown] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [apexTriggers, setApexTriggers] = useState<any[]>([])
  //const allData = store.salesforceAuditStore.getApexTriggersData()

  const salesforceAuditId = salesforceAuditStore.auditData?.salesforceAuditId

  useEffect(() => {
    const fetchApexTriggers = async () => {
      if (!salesforceAuditId) {
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        const response = await getSalesforceApexTriggers({
          id: salesforceAuditId
        })
        const apexTriggers = response.data.map((item: any) => item.record)

        salesforceAuditStore.setApexTriggersData(response?.data)
        setApexTriggers(apexTriggers) // set single profile or change logic for multiple
      } catch (err: any) {
        console.error('Error fetching profiles:', err)
      } finally {
        setLoading(false)
      }
    }

    fetchApexTriggers()
  }, [salesforceAuditId])

  const activeData = apexTriggers?.filter(
    (item: any) => item.Status === 'Active'
  )
  const inactiveData = apexTriggers?.filter(
    (item: any) => item.Status === 'Inactive'
  )

  const [sortConfig, setSortConfig] = useState<{
    key: string | null
    direction: 'asc' | 'desc' | null
  }>({
    key: null,
    direction: null
  })

  const toggleSort = (key: string) => {
    setSortConfig((prev) => {
      if (prev.key !== key) return { key, direction: 'asc' }
      if (prev.direction === 'asc') return { key, direction: 'desc' }
      return { key: null, direction: null }
    })
  }

  const activeFilteredData = useMemo(() => {
    const trimmedSearch = activeSearchTerm
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ')
    if (!trimmedSearch) return activeData

    return activeData.filter((item: any) => {
      const name = item?.Name?.toLowerCase() || ''
      const modifiedBy = item?.LastModifiedBy?.Name?.toLowerCase() || ''
      const author = item?.CreatedBy?.Name?.toLowerCase() || ''

      const status = item?.Status?.toLowerCase() || ''

      let eventType = ''
      try {
        eventType =
          salesforceAuditStore
            .getApexTriggerEventString(item?.Id)
            ?.toLowerCase() || ''
      } catch (e) {
        console.warn('Error getting eventType for item.Id', item?.Id, e)
      }

      let formattedDates: string[] = []
      if (item?.LastModifiedDate) {
        const m = moment(item.LastModifiedDate)
        formattedDates = [
          m.format('MMMM D YYYY').toLowerCase(), // august 5 2025
          m.format('D MMMM YYYY').toLowerCase(), // 5 august 2025
          m.format('MMM D').toLowerCase(), // aug 5
          m.format('D MMM').toLowerCase(), // 5 aug
          m.format('MMMM D').toLowerCase(), // august 5
          m.format('D MMMM').toLowerCase(), // 5 august
          m.format('YYYY').toLowerCase(), // 2025
          m.format('MMMM').toLowerCase(), // august
          m.format('D').toLowerCase() // 5
        ]
      }

      const matchesDate = formattedDates.some((date) =>
        date.includes(trimmedSearch)
      )

      // --- Column-specific search ---
      switch (searchColumn) {
        case 'Name':
          return name.includes(trimmedSearch)
        case 'LastModifiedBy':
          return modifiedBy.includes(trimmedSearch)
        case 'CreatedBy':
          return author.includes(trimmedSearch)
        case 'Status':
          return status.includes(trimmedSearch)
        case 'LastModifiedDate':
          return matchesDate
        case 'Id':
          return eventType.includes(trimmedSearch)
        case 'all':
        default:
          return (
            name.includes(trimmedSearch) ||
            modifiedBy.includes(trimmedSearch) ||
            author.includes(trimmedSearch) ||
            status.includes(trimmedSearch) ||
            eventType.includes(trimmedSearch) ||
            matchesDate
          )
      }
    })
  }, [activeSearchTerm, activeData, searchColumn])

  // --- Sorting ---
  const activeSortedData = useMemo(() => {
    if (!sortConfig.key || !sortConfig.direction) return activeFilteredData

    return [...activeFilteredData].sort((a: any, b: any) => {
      const dir = sortConfig.direction === 'asc' ? 1 : -1

      if (sortConfig.key === 'Id') {
        const aEvent =
          salesforceAuditStore
            .getApexTriggerEventString(a?.Id)
            ?.toLowerCase() || ''
        const bEvent =
          salesforceAuditStore
            .getApexTriggerEventString(b?.Id)
            ?.toLowerCase() || ''
        return aEvent.localeCompare(bEvent) * dir
      }

      if (sortConfig.key === 'LastModifiedDate') {
        const dateA = new Date(a?.LastModifiedDate || 0).getTime()
        const dateB = new Date(b?.LastModifiedDate || 0).getTime()
        return (dateA - dateB) * dir
      }

      if (sortConfig.key === 'LastModifiedBy') {
        const aMod = a?.LastModifiedBy?.Name?.toLowerCase() || ''
        const bMod = b?.LastModifiedBy?.Name?.toLowerCase() || ''
        return aMod.localeCompare(bMod) * dir
      }

      if (sortConfig.key === 'CreatedBy') {
        const aAuthor = a?.CreatedBy?.Name?.toLowerCase() || ''
        const bAuthor = b?.CreatedBy?.Name?.toLowerCase() || ''
        return aAuthor.localeCompare(bAuthor) * dir
      }

      const aVal = a?.[sortConfig.key!] || ''
      const bVal = b?.[sortConfig.key!] || ''

      if (!isNaN(Number(aVal)) && !isNaN(Number(bVal))) {
        return (Number(aVal) - Number(bVal)) * dir
      }

      return String(aVal).localeCompare(String(bVal)) * dir
    })
  }, [activeFilteredData, sortConfig, salesforceAuditStore])

  const inActiveFilteredData = useMemo(() => {
    const trimmedSearch = activeSearchTerm
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ')
    if (!trimmedSearch) return inactiveData

    return inactiveData.filter((item: any) => {
      const name = item?.Name?.toLowerCase() || ''
      const modifiedBy = item?.LastModifiedBy?.Name?.toLowerCase() || ''
      const author = item?.CreatedBy?.Name?.toLowerCase() || ''

      const status = item?.Status?.toLowerCase() || ''
      const eventType =
        salesforceAuditStore.getApexTriggerEventString(item)?.toLowerCase() ||
        ''

      let formattedDates: string[] = []

      if (item?.LastModifiedDate) {
        const m = moment(item.LastModifiedDate)
        formattedDates = [
          m.format('MMMM D YYYY').toLowerCase(),
          m.format('D MMMM YYYY').toLowerCase(),
          m.format('MMM D').toLowerCase(),
          m.format('D MMM').toLowerCase(),
          m.format('MMMM D').toLowerCase(),
          m.format('D MMMM').toLowerCase(),
          m.format('YYYY').toLowerCase(),
          m.format('MMMM').toLowerCase(),
          m.format('D').toLowerCase()
        ]
      }

      const matchesDate = formattedDates.some((date) =>
        date.includes(trimmedSearch)
      )

      // --- Column-specific search ---
      switch (searchColumn) {
        case 'Name':
          return name.includes(trimmedSearch)
        case 'LastModifiedBy':
          return modifiedBy.includes(trimmedSearch)
        case 'CreatedBy':
          return author.includes(trimmedSearch)
        case 'Status':
          return status.includes(trimmedSearch)
        case 'LastModifiedDate':
          return matchesDate
        case 'Id':
          return eventType.includes(trimmedSearch)
        case 'all':
        default:
          return (
            name.includes(trimmedSearch) ||
            modifiedBy.includes(trimmedSearch) ||
            author.includes(trimmedSearch) ||
            status.includes(trimmedSearch) ||
            eventType.includes(trimmedSearch) ||
            matchesDate
          )
      }
    })
  }, [activeSearchTerm, inactiveData, salesforceAuditStore, searchColumn])

  const inActiveSortedData = useMemo(() => {
    if (!sortConfig.key || !sortConfig.direction) return inActiveFilteredData

    return [...inActiveFilteredData].sort((a: any, b: any) => {
      const dir = sortConfig.direction === 'asc' ? 1 : -1

      if (sortConfig.key === 'Id') {
        const aEvent =
          salesforceAuditStore.getApexTriggerEventString(a)?.toLowerCase() || ''
        const bEvent =
          salesforceAuditStore.getApexTriggerEventString(b)?.toLowerCase() || ''
        return aEvent.localeCompare(bEvent) * dir
      }

      if (sortConfig.key === 'LastModifiedDate') {
        const dateA = new Date(a?.LastModifiedDate || 0).getTime()
        const dateB = new Date(b?.LastModifiedDate || 0).getTime()
        return (dateA - dateB) * dir
      }

      if (sortConfig.key === 'LastModifiedBy') {
        const aMod = a?.LastModifiedBy?.Name?.toLowerCase() || ''
        const bMod = b?.LastModifiedBy?.Name?.toLowerCase() || ''
        return aMod.localeCompare(bMod) * dir
      }

      if (sortConfig.key === 'CreatedBy') {
        const aAuthor = a?.CreatedBy?.Name?.toLowerCase() || ''
        const bAuthor = b?.CreatedBy?.Name?.toLowerCase() || ''
        return aAuthor.localeCompare(bAuthor) * dir
      }

      const aVal = a?.[sortConfig.key!] || ''

      const bVal = b?.[sortConfig.key!] || ''

      if (!isNaN(Number(aVal)) && !isNaN(Number(bVal))) {
        return (Number(aVal) - Number(bVal)) * dir
      }

      return String(aVal).localeCompare(String(bVal)) * dir
    })
  }, [activeFilteredData, sortConfig, salesforceAuditStore])

  useEffect(() => {
    const organizationId = store.scopeStore.getScope()[
      'organization-id'
    ] as string
    const projectId = store.scopeStore.getScope()['project-id'] as string
    const environmentId = store.scopeStore.getScope()[
      'environment-id'
    ] as string

    store.breadcrumbStore.setMultipleBreadcrumbsOptions([
      {
        label: 'Salesforce Audit',
        link: `${replaceUrl(
          routeConstants.SALESFORCE_AUDIT_DASHBOARD,
          organizationId,
          projectId,
          environmentId
        )}?page=1&tab=1`
      },
      {
        label: (
          <div
            onClick={(e) => {
              e.stopPropagation()
              e.preventDefault()
            }}
            className='cursor-not-allowed'
          >
            <div
              onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
              }}
              className='pointer-events-none'
            >
              Apex Triggers
            </div>
          </div>
        ),
        link: replaceUrl(
          routeConstants.SALESFORCE_AUDIT_METADATA_OVERVIEW + '/apex-triggers',
          organizationId,
          projectId,
          environmentId
        )
      },
      {
        label: tabIndex === '1' ? 'Active' : 'Inactive',
        link: replaceUrl(
          routeConstants.SALESFORCE_AUDIT_METADATA_OVERVIEW + '/apex-triggers',
          organizationId,
          projectId,
          environmentId
        )
      }
    ])
  }, [tabIndex])

  const downloadApexTriggers = async () => {
    store.uiStore.setGlobalLoader(true)
    setLoading(true)

    const payload = {
      salesforceAuditId: salesforceAuditId as string,
      isActive: tabIndex === '1' ? true : false
    }

    try {
      const response = await downloadSalesforceApexTriggersReport(payload)
      const orgTZ = store.userStore.getOrganizationPreference().timeZone
      const apexTriggers: any[] = response.data || []

      const mappedClasses = apexTriggers.map((cls) => {
        return {
          TriggerName: cls?.name || '-',
          EventType: store.salesforceAuditStore.getApexTriggerEventString(
            cls?.id
          ),
          LastModifiedDate:
            cls?.lastModifiedDate && cls?.lastModifiedDate !== '-'
              ? timeZone(
                  orgTZ,
                  cls?.lastModifiedDate,
                  timeFormats.fullMonthDateYearTime
                )
              : '-',
          LastModifiedBy: cls?.lastModifiedBy?.Name || '-',
          Author: cls?.createdBy?.Name || '-'
        }
      })

      const date = new Date()
      const formattedDate = `${(date.getMonth() + 1)
        .toString()
        .padStart(
          2,
          '0'
        )}-${date.getDate().toString().padStart(2, '0')}-${date.getFullYear()}`

      convertJsonToCsvDownload(
        mappedClasses,
        `Salesforce_ApexTriggers_Report_${formattedDate}.csv`
      )

      ToastNotification({
        type: 'success',
        message: `Successfully exported ${mappedClasses.length} Apex Triggers`
      })
    } catch (error: any) {
      ToastNotification({
        type: 'error',
        message:
          typeof error === 'string'
            ? error
            : error?.message || 'Error downloading Apex Triggers report'
      })
    } finally {
      store.uiStore.setGlobalLoader(false)
      setLoading(false)
    }
  }

  const activeColumns = [
    {
      columnHeaderStyle: { width: '180px' },
      columnDataStyle: { width: '180px' },
      heading: (
        <div className='flex whitespace-nowrap space-x-[8px] text-left justify-left items-left'>
          <div>
            <DDSTypography.Paragraph
              variant='semiBold'
              size='caption'
              className='text-left flex whitespace-nowrap'
              color={pallete.colors.textDark6}
            >
              Trigger Name
            </DDSTypography.Paragraph>
          </div>
          <div
            className='flex flex-col justify-left -space-y-[6px]'
            onClick={() => toggleSort('Name')}
          >
            <CaretUp
              size={12}
              style={{
                color:
                  sortConfig.key === 'Name' && sortConfig.direction === 'asc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
            <CaretDown
              size={12}
              style={{
                color:
                  sortConfig.key === 'Name' && sortConfig.direction === 'desc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
          </div>
        </div>
      ),
      dataLabel: 'Name',
      render: (text: any) => (
        <div className='flex justify-start max-w-[180px] text-start row items-start '>
          <div>
            <Tooltip
              id={`tooltip-name-${text}`}
              label={text}
              className={`override-tooltip-arrow max-w-[90%] break-all ${
                typeof text === 'string' && text.length > 20 ? text : '!hidden'
              }`}
            >
              <div>
                <DDSTypography.Paragraph
                  size='para'
                  variant='medium'
                  color={pallete.colors.textDark4}
                  className='tooltip-container sm:block overflow-hidden truncate max-w-[180px]'
                >
                  {text || text === 0 ? text : '-'}
                </DDSTypography.Paragraph>
              </div>
            </Tooltip>
          </div>
        </div>
      )
    },
    {
      columnHeaderStyle: {},
      columnDataStyle: {},
      heading: (
        <div className='flex whitespace-nowrap space-x-[8px] text-left justify-left items-left'>
          <div>
            <DDSTypography.Paragraph
              variant='semiBold'
              size='caption'
              className='text-left flex whitespace-nowrap'
              color={pallete.colors.textDark6}
            >
              Event Type
            </DDSTypography.Paragraph>
          </div>
          <div
            className='flex flex-col justify-left -space-y-[6px]'
            onClick={() => toggleSort('Id')}
          >
            <CaretUp
              size={12}
              style={{
                color:
                  sortConfig.key === 'Id' && sortConfig.direction === 'asc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
            <CaretDown
              size={12}
              style={{
                color:
                  sortConfig.key === 'Id' && sortConfig.direction === 'desc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
          </div>
        </div>
      ),
      dataLabel: 'Id',
      render: (text: any) => {
        return (
          <div className='flex justify-left text-left row items-left'>
            <Tooltip
              id={`tooltip-name-${text}`}
              label={store.salesforceAuditStore.getApexTriggerEventString(text)}
              className={`override-tooltip-arrow max-w-[90%] break-all ${
                typeof store.salesforceAuditStore.getApexTriggerEventString(
                  text
                ) === 'string' &&
                store.salesforceAuditStore.getApexTriggerEventString(text)
                  .length > 20
                  ? store.salesforceAuditStore.getApexTriggerEventString(text)
                  : '!hidden'
              }`}
            >
              <DDSTypography.Paragraph
                size='para'
                variant='medium'
                color={pallete.colors.textDark4}
                className='tooltip-container sm:block overflow-hidden truncate min-w-[140px] max-w-[140px]'
              >
                {store.salesforceAuditStore.getApexTriggerEventString(text) ||
                  '-'}
              </DDSTypography.Paragraph>
            </Tooltip>
          </div>
        )
      }
    },

    {
      columnHeaderStyle: {},
      columnDataStyle: {},
      heading: (
        <div className='flex whitespace-nowrap space-x-[8px] text-left justify-left items-left'>
          <div>
            <DDSTypography.Paragraph
              variant='semiBold'
              size='caption'
              className='text-left flex whitespace-nowrap'
              color={pallete.colors.textDark6}
            >
              Last Modified
            </DDSTypography.Paragraph>
          </div>
          <div
            className='flex flex-col justify-left -space-y-[6px]'
            onClick={() => toggleSort('LastModifiedDate')}
          >
            <CaretUp
              size={12}
              style={{
                color:
                  sortConfig.key === 'LastModifiedDate' &&
                  sortConfig.direction === 'asc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
            <CaretDown
              size={12}
              style={{
                color:
                  sortConfig.key === 'LastModifiedDate' &&
                  sortConfig.direction === 'desc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
          </div>
        </div>
      ),
      dataLabel: 'LastModifiedDate',
      render: (text: any) => (
        <div className='flex justify-start text-start row items-start'>
          <DDSTypography.Paragraph
            size='para'
            variant='medium'
            color={pallete.colors.textDark4}
          >
            {text
              ? moment(text).format(timeFormats.shortMonthDateYearTime)
              : '-'}
          </DDSTypography.Paragraph>
        </div>
      )
    },
    {
      columnHeaderStyle: {},
      columnDataStyle: {},
      heading: (
        <div className='flex whitespace-nowrap space-x-[8px] text-left justify-left items-left'>
          <div>
            <DDSTypography.Paragraph
              variant='semiBold'
              size='caption'
              className='text-left flex whitespace-nowrap'
              color={pallete.colors.textDark6}
            >
              Modified by
            </DDSTypography.Paragraph>
          </div>
          <div
            className='flex flex-col justify-left -space-y-[6px]'
            onClick={() => toggleSort('LastModifiedBy')}
          >
            <CaretUp
              size={12}
              style={{
                color:
                  sortConfig.key === 'LastModifiedBy' &&
                  sortConfig.direction === 'asc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
            <CaretDown
              size={12}
              style={{
                color:
                  sortConfig.key === 'LastModifiedBy' &&
                  sortConfig.direction === 'desc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
          </div>
        </div>
      ),
      dataLabel: 'LastModifiedBy',

      render: (record: any) => {
        const lastModifiedByName = record?.Name || '-'

        return (
          <div className='flex justify-start text-start items-start'>
            <Tooltip
              id={`tooltip-name-${lastModifiedByName}`}
              label={lastModifiedByName}
              className={`override-tooltip-arrow max-w-[90%] break-all ${
                typeof lastModifiedByName === 'string' &&
                lastModifiedByName.length > 16
                  ? ''
                  : '!hidden'
              }`}
            >
              <div>
                <DDSTypography.Paragraph
                  size='para'
                  variant='medium'
                  color={pallete.colors.textDark4}
                  className='tooltip-container sm:block overflow-hidden truncate min-w-[140px] max-w-[140px]'
                >
                  {lastModifiedByName}
                </DDSTypography.Paragraph>
              </div>
            </Tooltip>
          </div>
        )
      }
    },

    {
      columnHeaderStyle: {},
      columnDataStyle: {},
      heading: (
        <div className='flex whitespace-nowrap space-x-[8px] text-left justify-left items-left'>
          <div>
            <DDSTypography.Paragraph
              variant='semiBold'
              size='caption'
              className='text-left flex whitespace-nowrap'
              color={pallete.colors.textDark6}
            >
              Author
            </DDSTypography.Paragraph>
          </div>
          <div
            className='flex flex-col justify-left -space-y-[6px]'
            onClick={() => toggleSort('CreatedBy')}
          >
            <CaretUp
              size={12}
              style={{
                color:
                  sortConfig.key === 'CreatedBy' &&
                  sortConfig.direction === 'asc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
            <CaretDown
              size={12}
              style={{
                color:
                  sortConfig.key === 'CreatedBy' &&
                  sortConfig.direction === 'desc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
          </div>
        </div>
      ),
      dataLabel: 'CreatedBy',
      render: (record: any) => {
        const createdBy = record?.Name || '-'

        return (
          <div className='flex justify-start text-start items-start'>
            <Tooltip
              id={`tooltip-name-${createdBy}`}
              label={createdBy}
              className={`override-tooltip-arrow max-w-[90%] break-all ${
                typeof createdBy === 'string' && createdBy.length > 16
                  ? ''
                  : '!hidden'
              }`}
            >
              <div>
                <DDSTypography.Paragraph
                  size='para'
                  variant='medium'
                  color={pallete.colors.textDark4}
                  className='tooltip-container sm:block overflow-hidden truncate min-w-[140px] max-w-[140px]'
                >
                  {createdBy}
                </DDSTypography.Paragraph>
              </div>
            </Tooltip>
          </div>
        )
      }
    },

    {
      columnHeaderStyle: { width: '20px' },
      columnDataStyle: { width: '20px' },
      heading: '',
      dataLabel: 'Id',
      render: (text: any) => {
        return (
          <div className='cursor-pointer'>
            <ChevronRight
              size={16}
              onClick={() => {
                navigate(`${text}?tab=${tabIndex}`)
              }}
              color={pallete.colors.FillButton}
            />
          </div>
        )
      }
    }
  ]

  const inActiveColumns = [
    {
      columnHeaderStyle: { width: '180px' },
      columnDataStyle: { width: '180px' },
      heading: (
        <div className='flex whitespace-nowrap space-x-[8px] text-left justify-left items-left'>
          <div>
            <DDSTypography.Paragraph
              variant='semiBold'
              size='caption'
              className='text-left flex whitespace-nowrap'
              color={pallete.colors.textDark6}
            >
              Trigger Name
            </DDSTypography.Paragraph>
          </div>
          <div
            className='flex flex-col justify-left -space-y-[6px]'
            onClick={() => toggleSort('Name')}
          >
            <CaretUp
              size={12}
              style={{
                color:
                  sortConfig.key === 'Name' && sortConfig.direction === 'asc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
            <CaretDown
              size={12}
              style={{
                color:
                  sortConfig.key === 'Name' && sortConfig.direction === 'desc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
          </div>
        </div>
      ),
      dataLabel: 'Name',
      render: (text: any) => (
        <div className='flex justify-start max-w-[180px] text-start row items-start '>
          <div>
            <Tooltip
              id={`tooltip-name-${text}`}
              label={text}
              className={`override-tooltip-arrow max-w-[90%] break-all ${
                typeof text === 'string' && text.length > 18 ? text : '!hidden'
              }`}
            >
              <div>
                <DDSTypography.Paragraph
                  size='para'
                  variant='medium'
                  color={pallete.colors.textDark4}
                  className='tooltip-container sm:block overflow-hidden truncate max-w-[180px]'
                >
                  {text || text === 0 ? text : '-'}
                </DDSTypography.Paragraph>
              </div>
            </Tooltip>
          </div>
        </div>
      )
    },
    {
      columnHeaderStyle: {},
      columnDataStyle: {},
      heading: (
        <div className='flex whitespace-nowrap space-x-[8px] text-left justify-left items-left'>
          <div>
            <DDSTypography.Paragraph
              variant='semiBold'
              size='caption'
              className='text-left flex whitespace-nowrap'
              color={pallete.colors.textDark6}
            >
              Event Type
            </DDSTypography.Paragraph>
          </div>
          <div
            className='flex flex-col justify-left -space-y-[6px]'
            onClick={() => toggleSort('Id')}
          >
            <CaretUp
              size={12}
              style={{
                color:
                  sortConfig.key === 'Id' && sortConfig.direction === 'asc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
            <CaretDown
              size={12}
              style={{
                color:
                  sortConfig.key === 'Id' && sortConfig.direction === 'desc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
          </div>
        </div>
      ),
      dataLabel: 'Id',
      render: (text: any) => {
        return (
          <div className='flex justify-left text-left row items-left'>
            <Tooltip
              id={`tooltip-name-${text}`}
              label={store.salesforceAuditStore.getApexTriggerEventString(text)}
              className={`override-tooltip-arrow max-w-[90%] break-all ${
                typeof store.salesforceAuditStore.getApexTriggerEventString(
                  text
                ) === 'string' &&
                store.salesforceAuditStore.getApexTriggerEventString(text)
                  .length > 20
                  ? store.salesforceAuditStore.getApexTriggerEventString(text)
                  : '!hidden'
              }`}
            >
              <DDSTypography.Paragraph
                size='para'
                variant='medium'
                color={pallete.colors.textDark4}
                className='tooltip-container sm:block overflow-hidden truncate min-w-[140px] max-w-[140px]'
              >
                {store.salesforceAuditStore.getApexTriggerEventString(text)}
              </DDSTypography.Paragraph>
            </Tooltip>
          </div>
        )
      }
    },

    {
      columnHeaderStyle: {},
      columnDataStyle: {},
      heading: (
        <div className='flex whitespace-nowrap space-x-[8px] text-left justify-left items-left'>
          <div>
            <DDSTypography.Paragraph
              variant='semiBold'
              size='caption'
              className='text-left flex whitespace-nowrap'
              color={pallete.colors.textDark6}
            >
              Last Modified
            </DDSTypography.Paragraph>
          </div>
          <div
            className='flex flex-col justify-left -space-y-[6px]'
            onClick={() => toggleSort('LastModifiedDate')}
          >
            <CaretUp
              size={12}
              style={{
                color:
                  sortConfig.key === 'LastModifiedDate' &&
                  sortConfig.direction === 'asc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
            <CaretDown
              size={12}
              style={{
                color:
                  sortConfig.key === 'LastModifiedDate' &&
                  sortConfig.direction === 'desc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
          </div>
        </div>
      ),
      dataLabel: 'LastModifiedDate',
      render: (text: any) => (
        <div className='flex justify-start text-start row items-start'>
          <DDSTypography.Paragraph
            size='para'
            variant='medium'
            color={pallete.colors.textDark4}
          >
            {moment(text).format(timeFormats.shortMonthDateYearTime)}
          </DDSTypography.Paragraph>
        </div>
      )
    },
    {
      columnHeaderStyle: {},
      columnDataStyle: {},
      heading: (
        <div className='flex whitespace-nowrap space-x-[8px] text-left justify-left items-left'>
          <div>
            <DDSTypography.Paragraph
              variant='semiBold'
              size='caption'
              className='text-left flex whitespace-nowrap'
              color={pallete.colors.textDark6}
            >
              Modified by
            </DDSTypography.Paragraph>
          </div>
          <div
            className='flex flex-col justify-left -space-y-[6px]'
            onClick={() => toggleSort('LastModifiedBy')}
          >
            <CaretUp
              size={12}
              style={{
                color:
                  sortConfig.key === 'LastModifiedBy' &&
                  sortConfig.direction === 'asc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
            <CaretDown
              size={12}
              style={{
                color:
                  sortConfig.key === 'LastModifiedBy' &&
                  sortConfig.direction === 'desc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
          </div>
        </div>
      ),
      dataLabel: 'LastModifiedBy',
      render: (record: any) => {
        const lastModifiedByName = record?.Name || '-'
        return (
          <div className='flex justify-start text-start items-start'>
            <Tooltip
              id={`tooltip-name-${lastModifiedByName}`}
              label={lastModifiedByName}
              className={`override-tooltip-arrow max-w-[90%] break-all ${
                typeof lastModifiedByName === 'string' &&
                lastModifiedByName.length > 16
                  ? ''
                  : '!hidden'
              }`}
            >
              <div>
                <DDSTypography.Paragraph
                  size='para'
                  variant='medium'
                  color={pallete.colors.textDark4}
                  className='tooltip-container sm:block overflow-hidden truncate min-w-[140px] max-w-[140px]'
                >
                  {lastModifiedByName}
                </DDSTypography.Paragraph>
              </div>
            </Tooltip>
          </div>
        )
      }
    },

    {
      columnHeaderStyle: {},
      columnDataStyle: {},
      heading: (
        <div className='flex whitespace-nowrap space-x-[8px] text-left justify-left items-left'>
          <div>
            <DDSTypography.Paragraph
              variant='semiBold'
              size='caption'
              className='text-left flex whitespace-nowrap'
              color={pallete.colors.textDark6}
            >
              Author
            </DDSTypography.Paragraph>
          </div>
          <div
            className='flex flex-col justify-left -space-y-[6px]'
            onClick={() => toggleSort('CreatedBy')}
          >
            <CaretUp
              size={12}
              style={{
                color:
                  sortConfig.key === 'CreatedBy' &&
                  sortConfig.direction === 'asc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
            <CaretDown
              size={12}
              style={{
                color:
                  sortConfig.key === 'CreatedBy' &&
                  sortConfig.direction === 'desc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
          </div>
        </div>
      ),
      dataLabel: 'CreatedBy',
      render: (record: any) => {
        const createdBy = record?.Name || '-'

        return (
          <div className='flex justify-start text-start items-start'>
            <Tooltip
              id={`tooltip-name-${createdBy}`}
              label={createdBy}
              className={`override-tooltip-arrow max-w-[90%] break-all ${
                typeof createdBy === 'string' && createdBy.length > 16
                  ? ''
                  : '!hidden'
              }`}
            >
              <div>
                <DDSTypography.Paragraph
                  size='para'
                  variant='medium'
                  color={pallete.colors.textDark4}
                  className='tooltip-container sm:block overflow-hidden truncate min-w-[140px] max-w-[140px]'
                >
                  {createdBy}
                </DDSTypography.Paragraph>
              </div>
            </Tooltip>
          </div>
        )
      }
    },

    {
      columnHeaderStyle: { width: '20px' },
      columnDataStyle: { width: '20px' },
      heading: '',
      dataLabel: 'Id',
      render: (text: any) => {
        return (
          <div className='cursor-pointer'>
            <ChevronRight
              size={16}
              onClick={() => {
                navigate(`${text}?tab=${tabIndex}`)
              }}
              color={pallete.colors.FillButton}
            />
          </div>
        )
      }
    }
  ]

  const tabs = [
    {
      id: '1',
      label:
        tabIndex === '1' ? (
          <DDSTypography.Title
            type='h5'
            variant='semiBold'
            color={pallete.colors.primary2}
          >
            Active ({activeData.length})
          </DDSTypography.Title>
        ) : (
          <DDSTypography.Title
            type='h5'
            variant='medium'
            color={pallete.colors.textDark5}
          >
            Active ({activeData.length})
          </DDSTypography.Title>
        ),
      children: (
        <div className='flex flex-col py-[24px]'>
          <div className='relative pb-[12px]'>
            <Search
              id='search'
              name='Search'
              placeholder='Search'
              value={searchTerm}
              onChange={(e) => {
                const value = e.target.value
                setSearchTerm(value)
                setShowDropdown(value.length > 0)
                if (value.trim() === '') {
                  setActiveSearchTerm('')
                }
              }}
              autoComplete='off'
              width='100%'
              onPressEnter={() => {
                setActiveSearchTerm(searchTerm.trim())
                setCurrentPage(1)
                setShowDropdown(false)
              }}
              onBlur={() => {
                setTimeout(() => {
                  setShowDropdown(false)
                  setCurrentPage(1)
                }, 200)
              }}
            />

            {showDropdown && (
              <div className='absolute z-[99999] mt-[4px] w-full bg-white border border-gray-200 rounded-md shadow-md'>
                {[
                  { label: 'Trigger Name', value: 'Name' },
                  { label: 'Event Type', value: 'Id' },
                  { label: 'Last Modified Date', value: 'LastModifiedBy' },
                  { label: 'Modified by', value: 'LastModifiedBy' },
                  { label: 'Author', value: 'CreatedBy' },
                  { label: 'All Columns', value: 'all' }
                ].map((option) => (
                  <div
                    key={option.value}
                    onClick={() => {
                      setSearchColumn(option.value)
                      setCurrentPage(1)
                      setActiveSearchTerm(searchTerm)
                      setShowDropdown(false)
                    }}
                    className='px-4 py-2 cursor-pointer hover:bg-gray-100 text-sm text-gray-800'
                  >
                    <span className='font-mulish text-black'>{searchTerm}</span>
                    <span className='ml-[12px] px-[4px] py-[2px] rounded-md bg-gray-100 text-gray-600 text-xs font-medium'>
                      in: {option.label}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
          {activeSortedData?.length > 0 ? (
            <Table
              columns={activeColumns}
              data={paginate(
                activeSortedData,
                currentPageActive,
                SalesforceAuditStore.pageSize
              )}
              totalCount={activeSortedData?.length}
              currentPage={currentPageActive}
              rowsPerPage={SalesforceAuditStore.pageSize}
              onPageChange={(page) => setCurrentPageActive(page)}
              clickable={(id) => {
                navigate(`${id}?tab=${tabIndex}`)
              }}
              clickType='SalesforceAudit'
            />
          ) : searchTerm?.trim() ? (
            <NoRecord
              imageSrc={NoRecordImage}
              style={{ height: '45vh' }}
              text={<div>No Apex Triggers match your search.</div>}
            />
          ) : (
            <NoRecord
              imageSrc={NoRecordData}
              style={{ height: '45vh' }}
              text={<div>No Apex Triggers have been created yet</div>}
            />
          )}
        </div>
      )
    },
    {
      id: '2',
      label:
        tabIndex === '2' ? (
          <DDSTypography.Title
            type='h5'
            variant='semiBold'
            color={pallete.colors.primary2}
          >
            Inactive ({inactiveData.length})
          </DDSTypography.Title>
        ) : (
          <DDSTypography.Title
            type='h5'
            variant='medium'
            color={pallete.colors.textDark5}
          >
            Inactive ({inactiveData.length})
          </DDSTypography.Title>
        ),
      children: (
        <div className='flex flex-col py-[24px]'>
          <div className='relative pb-[12px]'>
            <Search
              id='search'
              name='Search'
              placeholder='Search'
              value={searchTerm}
              onChange={(e) => {
                const value = e.target.value
                setSearchTerm(value)
                setShowDropdown(value.length > 0)
                if (value.trim() === '') {
                  setActiveSearchTerm('')
                }
              }}
              autoComplete='off'
              width='100%'
              onPressEnter={() => {
                setActiveSearchTerm(searchTerm.trim())
                setCurrentPage(1)
                setShowDropdown(false)
              }}
              onBlur={() => {
                setTimeout(() => {
                  setShowDropdown(false)
                  setCurrentPage(1)
                }, 200)
              }}
            />

            {showDropdown && (
              <div className='absolute z-[99999] mt-[4px] w-full bg-white border border-gray-200 rounded-md shadow-md'>
                {[
                  { label: 'Trigger Name', value: 'Name' },
                  { label: 'Event Type', value: 'Id' },
                  { label: 'Last Modified Date', value: 'LastModifiedDate' },
                  { label: 'Modified by', value: 'LastModifiedBy' },
                  { label: 'Author', value: 'CreatedBy' },
                  { label: 'All Columns', value: 'all' }
                ].map((option) => (
                  <div
                    key={option.value}
                    onClick={() => {
                      setSearchColumn(option.value)
                      setCurrentPage(1)
                      setActiveSearchTerm(searchTerm)
                      setShowDropdown(false)
                    }}
                    className='px-4 py-2 cursor-pointer hover:bg-gray-100 text-sm text-gray-800'
                  >
                    <span className='font-mulish text-black'>{searchTerm}</span>
                    <span className='ml-[12px] px-[4px] py-[2px] rounded-md bg-gray-100 text-gray-600 text-xs font-medium'>
                      in: {option.label}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
          {inActiveSortedData?.length > 0 ? (
            <Table
              columns={inActiveColumns}
              data={paginate(
                inActiveSortedData,
                currentPageInactive,
                SalesforceAuditStore.pageSize
              )}
              totalCount={inActiveSortedData?.length}
              currentPage={currentPageInactive}
              rowsPerPage={SalesforceAuditStore.pageSize}
              onPageChange={(page) => setCurrentPageInactive(page)}
              clickable={(id) => {
                navigate(`${id}?tab=${tabIndex}`)
              }}
              clickType='SalesforceAudit'
            />
          ) : searchTerm?.trim() ? (
            <NoRecord
              imageSrc={NoRecordImage}
              style={{ height: '45vh' }}
              text={<div>No Apex Triggers match your search.</div>}
            />
          ) : (
            <NoRecord
              imageSrc={NoRecordData}
              style={{ height: '45vh' }}
              text={<div>No Apex Triggers have been created yet</div>}
            />
          )}
        </div>
      )
    }
  ]

  return (
    <>
      {loading || store.uiStore.getGlobalLoader() ? (
        <Spinner />
      ) : (
        <div className='flex flex-col w-full py-[24px] '>
          <Tabs
            tabOptionsStyle={{
              minWidth: 'fit-content',
              width: 'auto',
              display: 'flex',
              justifyContent: 'left',
              alignItems: 'left',
              padding: '8px 12px',
              marginRight: '8px'
            }}
            tabOptions={tabs}
            id='tabs-grouped-api'
            onChange={(index: any) => {
              setTabIndex(index.toString())
              setSearchParams({
                ...searchParams,
                tab: index.toString()
              })
            }}
            activeTab={tabIndex}
            exportOption={true}
            showActionButton={true}
            actionButtonLabel='Export'
            onActionButtonPress={downloadApexTriggers}
            actionButtonDisabled={
              store.uiStore.getGlobalLoader() ||
              (tabIndex === '1' && activeSortedData.length === 0) ||
              (tabIndex === '2' && inActiveSortedData?.length === 0)
            }
          />
        </div>
      )}
    </>
  )
}

export default observer(SFApexTriggersOverview)
